import type { ScanCourse } from './types';

export const COURSE_ID_MAPPING_ERROR_CODE = 'COURSE_ID_MAPPING_ERROR';
export const COURSE_ID_MAPPING_ERROR_MESSAGE =
  'This course needs to be refreshed from Classroom. Sync from Classroom and try again.';

const CLASSROOM_ID_FIELDS = [
  'classroomCourseId',
  'googleClassroomCourseId',
  'googleCourseId',
  'externalId',
  'sourceId',
  'classroomId',
] as const;

type CourseWithPossibleIds = ScanCourse & {
  googleClassroomCourseId?: string | null;
  googleCourseId?: string | null;
  externalId?: string | null;
  sourceId?: string | null;
  localId?: string | null;
  classroomId?: string | null;
  classroom?: { id?: string | null } | null;
  source?: string | null;
};

function cleanId(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

export function isLocalCourseId(courseId: string | null | undefined): boolean {
  return cleanId(courseId)?.startsWith('course_') ?? false;
}

export function resolveLocalCourseId(course: CourseWithPossibleIds): string {
  return cleanId(course.localId) ?? course.id;
}

export function resolveClassroomCourseId(course: CourseWithPossibleIds): string | null {
  const localId = resolveLocalCourseId(course);
  const candidates = [
    ...CLASSROOM_ID_FIELDS.map((field) => cleanId(course[field])),
    cleanId(course.classroom?.id),
  ];

  return (
    candidates.find((candidate) => {
      if (!candidate) return false;
      if (candidate === localId) return false;
      return !isLocalCourseId(candidate);
    }) ?? null
  );
}

export function isClassroomSyncedCourse(course: CourseWithPossibleIds): boolean {
  return (
    course.syncStatus === 'synced' ||
    course.source === 'classroom' ||
    resolveClassroomCourseId(course) !== null
  );
}

export function resolveCourseIds(course: CourseWithPossibleIds): {
  localCourseId: string;
  classroomCourseId: string | null;
  isClassroomSynced: boolean;
  isLocalOnly: boolean;
  canCallGoogleClassroom: boolean;
} {
  const localCourseId = resolveLocalCourseId(course);
  const classroomCourseId = resolveClassroomCourseId(course);
  const isClassroomSynced = isClassroomSyncedCourse(course);
  const isLocalOnly = !isClassroomSynced;

  return {
    localCourseId,
    classroomCourseId,
    isClassroomSynced,
    isLocalOnly,
    canCallGoogleClassroom: !!classroomCourseId && !isLocalCourseId(classroomCourseId),
  };
}
